// Block tree → Strudel source string.
//
// Deterministic: generating from the same block tree always produces
// identical output. This matters because we content-hash generated code
// to skip re-parsing the same song twice.
//
// Formatting rules:
// - One blank line between top-level statements
// - stack(...) on one line if total length < 80, else multi-line 4-space
//   indent per child
// - Method chains with ≥ 3 effects break onto separate lines
// - const bindings get their own line, no inline comments
// - Leading/trailing comments preserved verbatim on statements

import { patternSource } from './mini';
import { isConditionalEffect } from './types';
import type {
  ConditionalEffect,
  Effect,
  EffectArg,
  Expression,
  Numeric,
  PatternSlot,
  Song,
  Statement,
} from './types';

const LINE_WIDTH = 80;
const INDENT = '  ';

export function generateCode(song: Song): string {
  const parts: string[] = [];
  for (const stmt of song.statements) {
    const rendered = renderStatement(stmt);
    parts.push(rendered);
  }
  // Single blank line between statements; no trailing newline beyond one.
  return parts.join('\n\n') + '\n';
}

function renderStatement(stmt: Statement): string {
  // Free-floating comments render as themselves — no surrounding body,
  // no leading/trailing attachment (they have none).
  if (stmt.kind === 'comment') {
    return stmt.content;
  }
  const body = renderStatementBody(stmt);
  const leading = stmt.leadingComment ? stmt.leadingComment + '\n' : '';
  const trailing = stmt.trailingComment ? ' ' + stmt.trailingComment : '';
  return leading + body + trailing;
}

function renderStatementBody(
  stmt: Exclude<Statement, { kind: 'comment' }>,
): string {
  switch (stmt.kind) {
    case 'setcpm':
      return `setcpm(${renderNumeric(stmt.value)})`;
    case 'setcps':
      return `setcps(${renderNumeric(stmt.value)})`;
    case 'const':
      return `const ${stmt.name} = ${renderExpression(stmt.expr, 0)}`;
    case 'expression':
      return renderExpression(stmt.expr, 0);
    case 'samples':
      return `samples(${JSON.stringify(stmt.source)})`;
  }
}

// `indent` is the current indent level (spaces) this expression starts at.
function renderExpression(expr: Expression, indent: number): string {
  switch (expr.kind) {
    case 'voice.s':
    case 'voice.note': {
      const head =
        expr.kind === 'voice.s'
          ? `s(${renderPatternArg(expr.pattern)})`
          : `note(${renderPatternArg(expr.pattern)})`;
      return renderChain(head, expr.effects, indent);
    }
    case 'stack':
      return renderContainer('stack', expr.children, indent);
    case 'arrange':
      return renderArrange(expr.entries, indent);
    case 'ref':
      if (expr.effects && expr.effects.length > 0) {
        return renderChain(expr.name, expr.effects, indent);
      }
      return expr.name;
    case 'customFunction':
      return expr.source;
  }
}

function renderContainer(
  name: 'stack',
  children: Expression[],
  indent: number,
): string {
  const inlineChildren = children.map((c) => renderExpression(c, 0));
  const inline = `${name}(${inlineChildren.join(', ')})`;
  if (inline.length + indent <= LINE_WIDTH && !inlineChildren.some(hasNewline)) {
    return inline;
  }
  const pad = INDENT.repeat((indent / INDENT.length) + 1);
  const rendered = children.map((c) => pad + renderExpression(c, pad.length));
  return `${name}(\n${rendered.join(',\n')},\n${INDENT.repeat(indent / INDENT.length)})`;
}

function renderArrange(
  entries: { cycles: Numeric; section: Expression }[],
  indent: number,
): string {
  const pad = INDENT.repeat((indent / INDENT.length) + 1);
  const lines = entries.map((e) => {
    const cyc = renderNumeric(e.cycles);
    const sec = renderExpression(e.section, pad.length);
    // `[4,  intro]` — align cycles width to 2 chars for the common case.
    const cycStr = cyc.length < 2 ? cyc + ' '.repeat(2 - cyc.length) : cyc;
    return `${pad}[${cycStr}, ${sec}]`;
  });
  return `arrange(\n${lines.join(',\n')},\n${INDENT.repeat(indent / INDENT.length)})`;
}

function renderChain(head: string, effects: Effect[], indent: number): string {
  if (effects.length === 0) return head;
  const rendered = effects.map(renderEffect);
  const inline = head + rendered.join('');
  if (inline.length + indent <= LINE_WIDTH && effects.length < 3) {
    return inline;
  }
  const pad = INDENT.repeat((indent / INDENT.length) + 1);
  return head + '\n' + rendered.map((r) => pad + r).join('\n');
}

function renderEffect(effect: Effect): string {
  if (isConditionalEffect(effect)) return renderConditional(effect);
  const args = effect.args.map(renderEffectArg).join(', ');
  return `.${effect.kind}(${args})`;
}

function renderConditional(effect: ConditionalEffect): string {
  const cond = effect.condArgs.map(renderEffectArg);
  const body = renderConditionalBody(effect.body);
  const allArgs = [...cond, body].join(', ');
  return `.${effect.kind}(${allArgs})`;
}

function renderConditionalBody(effects: Effect[]): string {
  // Always use `x` as the placeholder name — conditional bodies never
  // capture outer scope in the block model, so the identifier is free
  // to be any valid one. A stable choice keeps generated code
  // deterministic under round-trip.
  const chain = effects.map(renderEffect).join('');
  return `x => x${chain}`;
}

function renderEffectArg(arg: EffectArg): string {
  switch (arg.kind) {
    case 'numeric':
      return renderNumeric(arg.value);
    case 'pattern':
      return renderPatternArg(arg.value);
    case 'string':
      return JSON.stringify(arg.value);
  }
}

function renderPatternArg(pattern: PatternSlot): string {
  return JSON.stringify(patternSource(pattern));
}

function renderNumeric(n: Numeric): string {
  switch (n.kind) {
    case 'num':
      return formatNumber(n.value);
    case 'binary': {
      const lhs = renderBinaryChild(n.lhs, n.op, 'left');
      const rhs = renderBinaryChild(n.rhs, n.op, 'right');
      return `${lhs} ${n.op} ${rhs}`;
    }
    case 'ref':
      return n.name;
    case 'raw':
      return n.source;
    case 'modulator': {
      // Sources with positional args (e.g. knob(N)) emit `name(args…)`.
      // Nullary sources (args === undefined) emit just `name` — Strudel
      // auto-calls them. An empty args array is preserved as `name()`
      // so a `args=[]` AST round-trips back to `args=[]` rather than
      // collapsing into a bare VariableName ref.
      let out = n.source;
      if (n.args !== undefined) {
        out += `(${n.args.map(renderNumeric).join(', ')})`;
      }
      if (n.range) {
        out += `.range(${renderNumeric(n.range[0])}, ${renderNumeric(n.range[1])})`;
      }
      if (n.slow !== undefined) out += `.slow(${renderNumeric(n.slow)})`;
      if (n.fast !== undefined) out += `.fast(${renderNumeric(n.fast)})`;
      return out;
    }
  }
}

// Parenthesise a binary child when precedence/associativity require
// it, so `(120 + 10) / 4` regenerates as `(120 + 10) / 4` rather than
// `120 + 10 / 4` (which reparses to a different tree). Rules:
//   - child precedence < parent precedence → always parenthesise
//   - child precedence == parent precedence AND child is on the right
//     of a non-commutative op (- or /) → parenthesise (`a - (b - c)`
//     is not `a - b - c`)
function renderBinaryChild(
  child: Numeric,
  parentOp: '+' | '-' | '*' | '/',
  side: 'left' | 'right',
): string {
  const rendered = renderNumeric(child);
  if (child.kind !== 'binary') return rendered;
  const childPrec = precedence(child.op);
  const parentPrec = precedence(parentOp);
  const nonCommutativeParent = parentOp === '-' || parentOp === '/';
  const needs =
    childPrec < parentPrec ||
    (childPrec === parentPrec && side === 'right' && nonCommutativeParent);
  return needs ? `(${rendered})` : rendered;
}

function precedence(op: '+' | '-' | '*' | '/'): number {
  return op === '+' || op === '-' ? 1 : 2;
}

function formatNumber(n: number): string {
  // Strudel songs mostly use short decimals; Number.toString handles it.
  // Avoid scientific notation for small numbers.
  if (Number.isInteger(n)) return String(n);
  return String(n);
}

function hasNewline(s: string): boolean {
  return s.includes('\n');
}
